import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface EmptyStateProps {
  title: string;
  message: string;
  actionText?: string;
  actionLink?: string;
  onAction?: () => void;
  secondaryActionText?: string;
  secondaryActionLink?: string;
  onSecondaryAction?: () => void;
  icon?: React.ReactNode;
  animationDuration?: number;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  message,
  actionText,
  actionLink,
  onAction,
  secondaryActionText,
  secondaryActionLink,
  onSecondaryAction,
  icon = <VisibilityIcon sx={{ fontSize: 60, opacity: 0.7, color: 'primary.main' }} />,
  animationDuration = 0.5
}) => {
  const navigate = useNavigate();

  const handleAction = () => {
    if (onAction) {
      onAction();
    } else if (actionLink) {
      navigate(actionLink);
    }
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction();
    } else if (secondaryActionLink) {
      navigate(secondaryActionLink);
    }
  };

  return (
    <Box 
      sx={{ 
        mt: 4, 
        textAlign: 'center',
        animation: `fadeIn ${animationDuration}s ease-in-out`,
        '@keyframes fadeIn': {
          '0%': {
            opacity: 0,
            transform: 'translateY(20px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}
    >
      <Paper
        elevation={2}
        sx={{
          bgcolor: 'background.paper',
          p: 4,
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.12)',
          maxWidth: 600,
          mx: 'auto',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
            transform: 'translateY(-4px)'
          }
        }}
      >
        <Box sx={{ mb: 3, animation: `pulse 2s infinite ease-in-out`, '@keyframes pulse': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.1)' },
          '100%': { transform: 'scale(1)' }
        }}}>
          {icon}
        </Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 450, mx: 'auto' }}>
          {message}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: { xs: 1, sm: 2 },
          mt: 2,
          justifyContent: 'center'
        }}>
          {(actionText && (onAction || actionLink)) && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleAction}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 500,
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 12px rgba(0, 0, 0, 0.3)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }
              }}
            >
              {actionText}
            </Button>
          )}
          
          {(secondaryActionText && (onSecondaryAction || secondaryActionLink)) && (
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={handleSecondaryAction}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 500,
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  backgroundColor: 'rgba(187, 134, 252, 0.08)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              {secondaryActionText}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default EmptyState; 